import ReactDOM from 'react-dom/client';
import 'react-phone-number-input/style.css';
import './App.css';

// 1. import `ChakraProvider` component
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import App from './routes.jsx';

import { configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';

import ReactGA from 'react-ga4';
import { Provider, useSelector } from 'react-redux';
import logger from 'redux-logger'; // Import redux-logger
import reducer from './redux/reducers.js';
import sagas from './redux/sagas.js';

import '@fontsource-variable/quicksand';

// create the saga middleware
const sagaMiddleware = createSagaMiddleware();

import './i18n';
import { theme as defaultTheme } from './theme/theme.jsx';

const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            thunk: true,
            serializableCheck: false,
            immutableCheck: false,
        })
            .concat(sagaMiddleware)
            .concat(logger), // Add logger here
    devTools: import.meta.env.DEV,
});

// then run the saga
sagaMiddleware.run(sagas);

// Measurement ID GA4
const TRACKING_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
if (TRACKING_ID) {
    ReactGA.initialize(TRACKING_ID);
}

function DynamicThemeProvider({ children }) {
    const { currentTheme } = useSelector((state) => state?.onlineSales);

    const theme = extendTheme(currentTheme || defaultTheme);

    return (
        <ChakraProvider theme={theme} key={JSON.stringify(currentTheme)}>
            {children}
        </ChakraProvider>
    );
}

const container = document.getElementById('root');
if (container && !container.__onlineSalesRoot) {
    container.__onlineSalesRoot = ReactDOM.createRoot(container);
}
const root = container ? container.__onlineSalesRoot : null;

root?.render(
    <Provider store={store}>
        <DynamicThemeProvider>
            <App />
        </DynamicThemeProvider>
    </Provider>,
);

// Global Error Handler for Chunk Loading Issues
window.addEventListener('error', (e) => {
    const resizeObserverLoopErrRe = /ResizeObserver loop limit exceeded/;
    const chunkFailedMessage = /Failed to fetch dynamically imported module/;

    if (resizeObserverLoopErrRe.test(e.message)) {
        e.stopImmediatePropagation();
    }

    if (chunkFailedMessage.test(e.message)) {
        if (!window.location.hash.includes('retry')) {
            window.location.reload();
        }
    }
});
